import React, { useState } from 'react'
import { CellRenderer } from '../../legacy-design-system/grid'
import { Route } from '../../graphql/schema/types/route'
import { css } from '@emotion/css'
import { Dialog, DialogFooter } from '../../legacy-design-system/modal'
import { FormattedMessage, useIntl } from 'react-intl'
import { RouteObservationResponse } from './api/route-observations'
import { useRouteObservations } from './use-route-observations'
import { LoadingTextMask } from '../../legacy-design-system/loading-masks'
import { WithTooltip } from '../../design-system'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { submitForm } from '../../util'
import { ellipsis } from 'polished'
import { Link } from '@contrast-security-inc/design-system-typography'
import { Button } from '@contrast-security-inc/design-system-buttons'

export const routeCellTestId = 'route-cell-test-id'
export const observationModalTestId = 'observation-modal-test-id'
export const anyVerbTooltipTestId = 'any-verb-tooltip-test-id'
export const showAllButtonTestId = 'show-all-button-test-id'

const maximumRouteObservations = 50

export function getRouteCellRenderer({
  organizationUuid,
  applicationUuid,
  aggregationSelections
}): CellRenderer<Route> {
  const { agentSessionId } = aggregationSelections
  return function routeCellRenderer(props) {
    return (
      <RouteCell
        organizationUuid={organizationUuid}
        applicationUuid={applicationUuid}
        agentSessionId={agentSessionId}
        {...props}
      />
    )
  }
}

export function RouteCell({
  organizationUuid,
  applicationUuid,
  rowData: { signature, hash },
  getCellContentProps,
  agentSessionId
}) {
  const [dialogIsOpen, setDialogIsOpen] = useState(false)

  return (
    <>
      <Link
        {...getCellContentProps({})}
        color="neutral100"
        className={css`
          line-height: 20px;
          margin-left: 10px;
          margin-right: 25px;
          ${ellipsis()}
        `}
        onClick={() => setDialogIsOpen(true)}
        data-testid={routeCellTestId}
      >
        {signature}
      </Link>
      <Dialog
        isOpen={dialogIsOpen}
        onCancel={() => setDialogIsOpen(false)}
        renderTitle={() => (
          <FormattedMessage id="title_applicationRouteCoverage_observations" />
        )}
        testId={observationModalTestId}
      >
        <DialogBody
          organizationUuid={organizationUuid}
          applicationUuid={applicationUuid}
          routeHash={hash}
          setDialogIsOpen={setDialogIsOpen}
          agentSessionId={agentSessionId}
        />
      </Dialog>
    </>
  )
}

function RouteObservationsListLoading() {
  const loadingContainerCss = css`
    margin-top: 7px;
  `

  return (
    <>
      <div className={loadingContainerCss}>
        <LoadingTextMask max={10} />
      </div>
      <div className={loadingContainerCss}>
        <LoadingTextMask max={10} />
      </div>
      <div className={loadingContainerCss}>
        <LoadingTextMask max={10} />
      </div>
      <div className={loadingContainerCss}>
        <LoadingTextMask max={10} />
      </div>
    </>
  )
}

export function RouteObservationsList({
  loading,
  observationsResponse
}: {
  readonly observationsResponse?: RouteObservationResponse
  readonly loading: boolean
}) {
  const intl = useIntl()

  if (loading || !observationsResponse) {
    return <RouteObservationsListLoading />
  }

  return observationsResponse.observations.length > 0 ? (
    <ul
      className={css`
        padding: 0 25px;
      `}
    >
      {observationsResponse.observations.map((observation, index) => {
        return (
          <li
            className={css`
              word-break: break-word;
              padding: 10px 0;
              width: fit-content;
            `}
            key={index}
          >
            {observation.verb}{' '}
            {observation.verb ? (
              observation.url
            ) : (
              <WithTooltip
                dataTestId={anyVerbTooltipTestId}
                width="fit-content"
                delayRender={false}
                renderTooltipContent={() => (
                  <FormattedMessage id="detail_applicationCoverageObservations_tooltip" />
                )}
                children={() => (
                  <>
                    {observation.url}
                    <span
                      className={css`
                        color: ${theme.color.turquoise70};
                      `}
                    >
                      *
                    </span>
                  </>
                )}
                tiny
              />
            )}
          </li>
        )
      })}
    </ul>
  ) : (
    <>
      {intl.formatMessage({
        id: 'detail_applicationsCoverage_noRouteObservations'
      })}
    </>
  )
}

function DialogBody({
  organizationUuid,
  applicationUuid,
  routeHash,
  setDialogIsOpen,
  agentSessionId
}: {
  readonly organizationUuid: string
  readonly applicationUuid: string
  readonly routeHash: string
  readonly setDialogIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  readonly agentSessionId?: string[]
}) {
  const intl = useIntl()

  const { data: observationsResponse, isLoading: observationsLoading } =
    useRouteObservations(
      organizationUuid,
      applicationUuid,
      routeHash,
      agentSessionId
    )

  return (
    <>
      <div
        className={css`
          margin: 20px;
        `}
      >
        <RouteObservationsList
          observationsResponse={observationsResponse}
          loading={observationsLoading}
        />
      </div>
      <DialogFooter>
        {observationsResponse &&
          observationsResponse.total > maximumRouteObservations && (
            <ExportObservationsButton
              organizationUuid={organizationUuid}
              applicationUuid={applicationUuid}
              routeHash={routeHash}
              agentSessionId={agentSessionId}
            />
          )}
        <Button onClick={() => setDialogIsOpen(false)}>
          {intl.formatMessage({ id: 'action_close', defaultMessage: 'Close' })}
        </Button>
      </DialogFooter>
    </>
  )
}

function ExportObservationsButton({
  organizationUuid,
  applicationUuid,
  routeHash,
  agentSessionId
}: {
  readonly organizationUuid: string
  readonly applicationUuid: string
  readonly routeHash: string
  readonly agentSessionId?: string[]
}) {
  const exportUrl = `${organizationUuid}/applications/${applicationUuid}/route/${routeHash}/observations/export/csv${
    agentSessionId ? '?agentSessionId=' + agentSessionId : ''
  }`

  return (
    <div data-testid={showAllButtonTestId}>
      <FormattedMessage
        id="action_showAll_keywords"
        children={(message) => (
          <Button
            className={css`
              margin-right: 5px;
            `}
            onClick={() => submitForm(exportUrl)}
          >
            {message}
          </Button>
        )}
      />
    </div>
  )
}
